import AssistChip from 'LuxKit/Chips/AssistChip'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import RadioInput from 'LuxKit/Radio/RadioInput'
import Group from 'components/utils/Group'
import useToggle from 'hooks/useToggle'
import { rem } from 'polished'
import React, { useCallback, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import useOfferListSortOptions from 'hooks/useOfferListSortOptions'

const RadioGroup = styled(Group)`
  padding-inline: ${rem(16)};
`

export function useCruiseSearchSortOptions() {
  const {
    SORT_OPTION_RECOMMENDED,
    SORT_OPTION_PRICE_ASC,
    SORT_OPTION_PRICE_DESC,
    SORT_DEPARTURE_SOON,
    SORT_NEWEST,
  } = useOfferListSortOptions()

  return useMemo(() => [
    SORT_OPTION_RECOMMENDED,
    SORT_OPTION_PRICE_ASC,
    SORT_OPTION_PRICE_DESC,
    SORT_DEPARTURE_SOON,
    SORT_NEWEST,
  ], [SORT_OPTION_RECOMMENDED, SORT_OPTION_PRICE_ASC, SORT_OPTION_PRICE_DESC, SORT_DEPARTURE_SOON, SORT_NEWEST])
}

interface Props {
  sort: string
  onChange: (newSort: App.OfferListSortByOptions) => void
}

function CruiseSortDropdown(props: Props) {
  const { onChange, sort } = props
  const intl = useIntl()
  const {
    SORT_OPTION_RECOMMENDED,
    SORT_OPTION_PRICE_ASC,
    SORT_OPTION_PRICE_DESC,
    SORT_DEPARTURE_SOON,
    SORT_NEWEST,
  } = useOfferListSortOptions()
  const CruiseSearchSortOptions = useCruiseSearchSortOptions()

  const [open, toggleSortOpen, , closeSort] = useToggle()
  const triggerRef = useRef<HTMLButtonElement>(null)

  const sortLabels: Record<string, string> = useMemo(() => ({
    [SORT_OPTION_RECOMMENDED.value]: intl.formatMessage({
      id: '-GM8jkcTsnHrO1129DKz',
      defaultMessage: 'Recommended',
      description: 'Sort option label for recommended cruise results in the cruises section',
    }),
    [SORT_OPTION_PRICE_ASC.value]: intl.formatMessage({
      id: 'nh6FgfDg51X_s1VD_l5l',
      defaultMessage: 'Price (Lowest)',
      description: 'Sort option label for lowest price first cruise results',
    }),
    [SORT_OPTION_PRICE_DESC.value]: intl.formatMessage({
      id: 'FtgB693oBRJ6axsxbV-m',
      defaultMessage: 'Price (Highest)',
      description: 'Sort option label for highest price first cruise results',
    }),
    [SORT_DEPARTURE_SOON.value]: intl.formatMessage({
      id: '7TE9LGCNQIZU24-YgRwZ',
      defaultMessage: 'Depart soon',
      description: 'Sort option label for soonest departure cruise results',
    }),
    [SORT_NEWEST.value]: intl.formatMessage({
      id: '61FA7f-HoJHPveTmnSrr',
      defaultMessage: 'Newest',
      description: 'Sort option label for newest cruise results in the cruise section',
    }),
  }), [intl, SORT_OPTION_RECOMMENDED, SORT_OPTION_PRICE_ASC, SORT_OPTION_PRICE_DESC, SORT_DEPARTURE_SOON, SORT_NEWEST])

  const onSortChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.currentTarget.value as App.OfferListSortByOptions)
    closeSort()
  }, [closeSort, onChange])

  const selectedLabel = useMemo(() => {
    const foundOption = CruiseSearchSortOptions.find(option => option.value === sort)
    if (foundOption) return sortLabels[foundOption.value] ?? foundOption.label
    return sortLabels[SORT_OPTION_RECOMMENDED.value]
  }, [sort, sortLabels, CruiseSearchSortOptions, SORT_OPTION_RECOMMENDED])

  const sortPrefix = intl.formatMessage({
    id: 'zaccWrBMR34KPDBUBB6n',
    defaultMessage: 'Sort: {selectedLabel}',
    description: 'Label for cruise sort dropdown chip showing selected sort option',
  }, { selectedLabel })

  return <>
    <AssistChip
      variant="filled"
      size="medium"
      onClick={toggleSortOpen}
      endIcon={<DropdownChevron open={open} />}
      ref={triggerRef}
    >
      {sortPrefix}
    </AssistChip>
    <DropdownList
      size="S"
      anchorRef={triggerRef}
      triggerRef={triggerRef}
      open={open}
      onClose={closeSort}
    >
      <RadioGroup direction="vertical" gap={16}>
        {CruiseSearchSortOptions.map(option => <RadioInput
          value={option.value}
          onChange={onSortChange}
          key={option.value}
          checked={option.value ? option.value === sort : !sort}
          name="sort"
        >
          {sortLabels[option.value] ?? option.label}
        </RadioInput>)}
      </RadioGroup>
    </DropdownList>
  </>
}

export default React.memo(CruiseSortDropdown)
